import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, TargetIcon, ListIcon, FileTextIcon, SettingsIcon, SparklesIcon, TrendingUpIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0
  }
};
export function MethodSection() {
  const methods = [{
    letter: 'R',
    title: 'Rápido',
    desc: 'Prompts listos para usar en segundos.',
    icon: <ZapIcon className="w-5 h-5 text-[#D4AF37]" data-id="element-320" />
  }, {
    letter: 'E',
    title: 'Específico',
    desc: 'Soluciones adaptadas a tu vida cotidiana.',
    icon: <TargetIcon className="w-5 h-5 text-[#D4AF37]" data-id="element-321" />
  }, {
    letter: 'S',
    title: 'Simple',
    desc: 'Sin complicaciones, directo al resultado.',
    icon: <ListIcon className="w-5 h-5 text-[#D4AF37]" data-id="element-322" />
  }, {
    letter: 'C',
    title: 'Concreto',
    desc: 'Ejemplos directos y claros, sin paja.',
    icon: <FileTextIcon className="w-5 h-5 text-[#D4AF37]" data-id="element-323" />
  }, {
    letter: 'A',
    title: 'Automatizado',
    desc: 'Mini flujos con herramientas que ya usas.',
    icon: <SettingsIcon className="w-5 h-5 text-[#D4AF37]" data-id="element-324" />
  }, {
    letter: 'T',
    title: 'Transformador',
    desc: 'Conviertes tareas complejas en simples.',
    icon: <SparklesIcon className="w-5 h-5 text-[#D4AF37]" data-id="element-325" />
  }, {
    letter: 'E',
    title: 'Eficiente',
    desc: 'Resultados medibles desde el primer día.',
    icon: <TrendingUpIcon className="w-5 h-5 text-[#D4AF37]" data-id="element-326" />
  }];
  return <section className="py-14 md:py-24 px-4 md:px-6 bg-[#0A0A0A] relative overflow-hidden border-t border-[#D4AF37]/10" data-id="element-327">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" data-id="element-328"></div>

      <div className="max-w-6xl mx-auto" data-id="element-329">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10 md:mb-16" data-id="element-330">
          <h2 className="text-2xl md:text-5xl font-serif font-bold text-[#E8E0D0] mb-4 md:mb-6" data-id="element-331">
            Te presentamos el Método{' '}
            <span className="text-[#D4AF37] italic drop-shadow-[0_0_15px_rgba(212,175,55,0.3)]" data-id="element-332">
              R.E.S.C.A.T.E.
            </span>
          </h2>
          <p className="text-base md:text-xl text-[#A09880] max-w-2xl mx-auto" data-id="element-333">
            La fórmula práctica para desbloquear el poder de la IA en tu día a
            día.
          </p>
        </motion.div>

        {/* Desktop: top row of 4, bottom row of 3 centered */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="hidden lg:block" data-id="element-334">
          {/* First row: R E S C */}
          <div className="grid grid-cols-4 gap-4 mb-4" data-id="element-335">
            {methods.slice(0, 4).map((method, i) => <MethodCard key={i} method={method} data-id="element-336" />)}
          </div>
          {/* Second row: A T E — centered */}
          <div className="grid grid-cols-4 gap-4" data-id="element-337">
            <div className="col-start-1 col-span-4 flex justify-center gap-4" data-id="element-338">
              {methods.slice(4).map((method, i) => <div key={i} className="flex-1 max-w-[calc(25%-12px)]" data-id="element-339">
                  <MethodCard method={method} data-id="element-340" />
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Tablet: 2 columns */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="hidden md:grid lg:hidden grid-cols-2 gap-3" data-id="element-341">
          {methods.map((method, i) => <MethodCard key={i} method={method} data-id="element-342" />)}
        </motion.div>

        {/* Mobile: single column, compact */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="md:hidden flex flex-col gap-2.5" data-id="element-343">
          {methods.map((method, i) => <MethodCard key={i} method={method} data-id="element-344" />)}
        </motion.div>
      </div>
    </section>;
}
function MethodCard({
  method
}: {
  method: {
    letter: string;
    title: string;
    desc: string;
    icon: React.ReactNode;
  };
}) {
  return <motion.div variants={itemVariants} className="bg-[#111111] border border-[#D4AF37]/20 rounded-xl p-3.5 md:p-5 flex flex-row items-center gap-3 md:gap-4 relative group overflow-hidden" data-id="element-345">
      <div className="absolute -right-4 -top-4 w-20 h-20 bg-[#D4AF37]/5 rounded-full blur-2xl group-hover:bg-[#D4AF37]/10 transition-all" data-id="element-346"></div>

      {/* Letter badge + icon */}
      <div className="flex items-center gap-2.5 shrink-0" data-id="element-347">
        <div className="w-10 h-10 md:w-12 md:h-12 bg-[#0A0A0A] border border-[#D4AF37]/40 rounded-lg md:rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(212,175,55,0.1)]" data-id="element-348">
          <span className="text-[#D4AF37] font-serif font-bold text-lg md:text-xl drop-shadow-[0_0_8px_rgba(212,175,55,0.4)]" data-id="element-349">
            {method.letter}
          </span>
        </div>
        <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-2 rounded-lg shadow-[0_0_10px_rgba(212,175,55,0.05)]" data-id="element-350">
          {method.icon}
        </div>
      </div>

      {/* Content */}
      <div className="min-w-0" data-id="element-351">
        <h3 className="text-base md:text-lg font-bold text-[#D4AF37] leading-tight mb-0.5" data-id="element-352">
          {method.title}
        </h3>
        <p className="text-xs md:text-sm text-[#A09880] leading-relaxed" data-id="element-353">
          {method.desc}
        </p>
      </div>
    </motion.div>;
}